import { Button } from "@/components/ui/button";
import { useCardBalance, useGetCards } from "@/hooks/use-queries";
import { formatAmount } from "@/lib/utils";
import { useModalStore } from "@/zustand/modalStore";
import { resolveCardStyle } from "@/pages/dashboard/shop/page";
import { Eye } from "lucide-react";
import { useState } from "react";

const Transfer2Wallet = ({ closeSheet }) => {
  const { data: cards, isLoading: cardIsLoading } = useGetCards();
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const { openModal } = useModalStore();

  return (
    <div className="text-white mt-4 px-2">
      <h2 className="font-semibold">Transfer to wallet</h2>

      <div className="mt-4">
        <h4 className="text-sm">Select card you want to withdraw from</h4>
        {cardIsLoading ? (
          <div className="relative mt-6 flex flex-col gap-4">
            {[1, 2, 3].map((index) => (
              <CardSkeleton key={index} />
            ))}
          </div>
        ) : cards?.data.length > 0 ? (
          <div>
            <div className="mt-6 flex flex-col gap-4">
              {cards?.data.map((card: Card) => (
                <Card
                  selectedCard={selectedCard}
                  setSelectedCard={setSelectedCard}
                  cardData={card}
                  key={card.id}
                />
              ))}
            </div>
            <div className="mt-6">
              <Button
                disabled={!selectedCard}
                onClick={() => {
                  openModal("fundWallet", { cardData: selectedCard });
                  closeSheet();
                }}
                className="text-primary-500 bg-dark-primary-main hover:bg-dark-primary-100 h-10 w-full"
              >
                Proceed
              </Button>
            </div>
          </div>
        ) : (
          <div className="w- flex h-100 items-center justify-center">
            <div className="flex flex-col text-center">
              <h2 className="text-2xl font-bold">No Cards</h2>
              <p className="text-white/40">
                Instantly create a card to start making transactions
              </p>

              {/* <div className="mt-4">
                <Button
                  className="w-full"
                  type="submit"
                  onClick={() => {
                    navigate("/dashboard/shop/virtual-cards");
                  }}
                >
                  Create Card
                </Button>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transfer2Wallet;

const CardSkeleton = () => {
  return (
    <div className="h-18 w-full max-w-lg animate-pulse overflow-hidden rounded-xl bg-gray-700 shadow-[rgba(0,_0,_0,_0.25)_0px_25px_50px_-12px]"></div>
  );
};

const Card = ({
  cardData,
  selectedCard,
  setSelectedCard,
}: {
  cardData: Card;
  selectedCard: Card | null;
  setSelectedCard: (card: Card) => void;
}) => {
  const { balance } = useCardBalance(cardData);
  const isActive = selectedCard?.id === cardData.id;
  const style = resolveCardStyle(cardData.bin, cardData.network);
  return (
    <div
      role="button"
      onClick={() => setSelectedCard(cardData)}
      tabIndex={0}
      className={`relative overflow-hidden rounded-xl border border-[#4D698B] px-4 py-2.5 transition-all hover:cursor-pointer active:scale-x-98 ${
        isActive ? "" : "opacity-50"
      }`}
      style={{ background: style.background }}
    >
      {style.blBlurImage && (
        <img
          className="absolute bottom-0 left-0"
          src={style.blBlurImage}
          alt=""
        />
      )}
      {style.tlBlurImage && (
        <img className="absolute top-0 left-0" src={style.tlBlurImage} alt="" />
      )}
      {style.trBlurImage && (
        <img
          className="absolute top-0 right-0"
          src={style.trBlurImage}
          alt=""
        />
      )}
      <img
        className="absolute top-0 right-0 z-10"
        src={style.linesImage ?? "/images/card-lines.svg"}
        alt=""
      />
      {isActive && (
        <div className="bg-primary-100 absolute top-2 right-2 z-20 size-3 rounded-full" />
      )}
      <div className="relative z-10 h-full w-full">
        <div className="mb-2 flex items-center gap-2">
          <img
            src={style.logoImage ?? "/images/logo-transparent-light.svg"}
            className="h-3 w-auto object-contain"
            alt="logo"
          />
          <p className="font-medium text-white">
            ${formatAmount(balance?.available)}
          </p>
          <Button
            variant={"ghost"}
            className="size-auto cursor-pointer !p-1 hover:bg-white/10"
          >
            <Eye className="size-4 text-white/40" />
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">**** **** **** {cardData.last4}</p>
            <p className="text-[10px] text-white/70">
              {cardData.firstName} {cardData.lastName}
            </p>
          </div>
          {cardData.network === "VISA" || cardData.network === "Visa" ? (
            <img className="w-10" src="/images/visa-white.png" alt="" />
          ) : (
            <img className="w-10" src="/images/mastercard-logo.svg" alt="" />
          )}
        </div>
      </div>
    </div>
  );
};
